import { useEffect, useState } from "react";
import { Inbox, Loader2, RefreshCw, Check, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import PaymentBreakdown from "@/components/booking/PaymentBreakdown";
import RentalAgreementModal from "@/components/booking/RentalAgreementModal";
import {
  BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES, BOOKING_STATUS_FILTERS,
  DURATION_UNIT_LABELS, formatRupiah, formatDate,
} from "@/components/booking/bookingMeta";

export default function OwnerBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState(null);
  const [agreementBooking, setAgreementBooking] = useState(null);

  const load = (status = filter) => {
    setLoading(true);
    setError(false);
    const qs = status && status !== "all" ? `?status=${status}` : "";
    apiClient
      .get(`/owner/bookings${qs}`)
      .then((r) => setBookings(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (id, action) => {
    setActing(id + action);
    try {
      await apiClient.post(`/owner/bookings/${id}/${action}`);
      toast.success(
        action === "approve" ? "Booking disetujui."
        : action === "reject" ? "Booking ditolak."
        : "Booking ditandai selesai."
      );
      load(filter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <OwnerLayout testid="owner-bookings-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Booking Masuk</h1>
          <p className="mt-2 text-sm text-muted-foreground">Kelola permintaan booking untuk property Anda.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="owner-bookings-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="owner-bookings-filters">
        {BOOKING_STATUS_FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            data-testid={`owner-bookings-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="owner-bookings-error">
          <p className="text-sm text-destructive">Gagal memuat booking.</p>
          <button onClick={() => load(filter)} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="owner-bookings-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><Inbox size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada booking masuk.</h3>
          <p className="mt-2 text-sm text-muted-foreground">Permintaan booking dari customer akan muncul di sini.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="owner-bookings-list">
          {bookings.map((b, i) => (
            <div key={b.id} data-testid={`owner-booking-${i}`}
              className="rounded-2xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold truncate">{b.property_name || "Hunian"}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${BOOKING_STATUS_STYLES[b.status] || ""}`} data-testid={`owner-booking-status-${i}`}>
                      {BOOKING_STATUS_LABELS[b.status] || b.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Kamar {b.room_number}{b.room_name ? ` · ${b.room_name}` : ""} · {b.duration} {DURATION_UNIT_LABELS[b.duration_unit]}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">Customer: <span className="text-foreground font-medium">{b.customer_name || "-"}</span></p>
                  <p className="mt-1 text-sm text-muted-foreground">{formatDate(b.start_date)} → {formatDate(b.end_date)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-heading text-xl font-bold text-primary" data-testid={`owner-booking-total-${i}`}>{formatRupiah(b.total_amount)}</p>
                  <p className="text-xs text-muted-foreground">Total kewajiban</p>
                  {b.status === "pending" && (
                    <div className="mt-3 flex gap-2 justify-end">
                      <button onClick={() => moderate(b.id, "approve")} disabled={acting === b.id + "approve"}
                        data-testid={`owner-booking-approve-${i}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors disabled:opacity-60">
                        {acting === b.id + "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Setujui
                      </button>
                      <button onClick={() => moderate(b.id, "reject")} disabled={acting === b.id + "reject"}
                        data-testid={`owner-booking-reject-${i}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-60">
                        {acting === b.id + "reject" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Tolak
                      </button>
                    </div>
                  )}
                  {b.status === "approved" && (
                    <div className="mt-3 flex justify-end">
                      <button onClick={() => moderate(b.id, "complete")} disabled={acting === b.id + "complete"}
                        data-testid={`owner-booking-complete-${i}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-sky-700 border border-sky-200 hover:bg-sky-50 transition-colors disabled:opacity-60">
                        {acting === b.id + "complete" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Tandai Selesai
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {(b.status === "approved" || b.status === "completed") && <PaymentBreakdown b={b} idPrefix={`owner-booking-${i}`} />}
              {["approved", "completed", "cancelled"].includes(b.status) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap rounded-xl border border-border bg-secondary/10 px-3 py-2">
                  <span className="text-sm font-medium text-foreground">Rental Agreement</span>
                  <button onClick={() => setAgreementBooking(b)}
                    data-testid={`owner-booking-agreement-${i}`}
                    className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
                    Lihat Agreement
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <RentalAgreementModal
        open={!!agreementBooking}
        bookingId={agreementBooking?.id}
        role="owner"
        onClose={() => setAgreementBooking(null)}
      />
    </OwnerLayout>
  );
}
